/**
 * Transform functions for EditVendorPageV2 component
 */
import { EMPTY_STRING } from './EditVendorPageV2.constants';
import { getNestedValue } from './EditVendorPageV2.helpers';

/**
 * Transform basic information from vendor data
 *
 * IMPORTANT: API response structure differs from payload structure:
 * - Payload sends: primary_poc_name, primary_poc_contact (flat structure)
 * - Response returns: contact_info.primary.name, contact_info.primary.contact (nested structure)
 * - Payload sends: vendor_email_id (flat)
 * - Response returns: vendor_email_id (flat) OR vendor_info.email (nested)
 * - Payload sends: vendor_address.address_line (nested)
 * - Response returns: vendor_address.address_line OR vendor_info.address.address_line (nested)
 *
 * This function handles both structures to ensure correct mapping.
 */
export const transformBasicInfo = (vendorData) => {
	// Debug: Log raw vendor data to verify structure
	console.log('🔄 [transformBasicInfo] Raw vendor data:', {
		vendor_email_id: vendorData?.vendor_email_id,
		'contact_info.primary.name': getNestedValue(vendorData, 'contact_info.primary.name'),
		'contact_info.primary.contact': getNestedValue(vendorData, 'contact_info.primary.contact'),
		'vendor_address.address_line': getNestedValue(vendorData, 'vendor_address.address_line'),
		'vendor_info.address.address_line': getNestedValue(
			vendorData,
			'vendor_info.address.address_line'
		),
	});

	const transformed = {
		vendor_type: vendorData?.vendor_type || EMPTY_STRING,
		vendor_name: vendorData?.vendor_name || EMPTY_STRING,
		legal_structure: vendorData?.legal_structure || EMPTY_STRING,
		vendor_email_id:
			vendorData?.vendor_email_id ||
			vendorData?.vendor_email ||
			getNestedValue(vendorData, 'vendor_info.email') ||
			EMPTY_STRING,
		primary_poc_name:
			vendorData?.primary_poc_name ||
			getNestedValue(vendorData, 'contact_info.primary.name') ||
			EMPTY_STRING,
		primary_poc_contact:
			vendorData?.primary_poc_contact ||
			getNestedValue(vendorData, 'contact_info.primary.contact') ||
			EMPTY_STRING,
		alternative_poc_name:
			vendorData?.alternative_poc_name ||
			getNestedValue(vendorData, 'contact_info.alternative.name') ||
			EMPTY_STRING,
		alternative_poc_contact:
			vendorData?.alternative_poc_contact ||
			getNestedValue(vendorData, 'contact_info.alternative.contact') ||
			EMPTY_STRING,
		vendor_address:
			getNestedValue(vendorData, 'vendor_address.address_line') ||
			getNestedValue(vendorData, 'vendor_info.address.address_line') ||
			vendorData?.vendor_address?.address_line ||
			vendorData?.vendor_info?.address?.address_line ||
			EMPTY_STRING,
		pincode:
			getNestedValue(vendorData, 'vendor_address.pincode') ||
			getNestedValue(vendorData, 'vendor_info.address.pincode') ||
			EMPTY_STRING,
		zone:
			getNestedValue(vendorData, 'vendor_address.zone') ||
			getNestedValue(vendorData, 'vendor_info.address.zone') ||
			EMPTY_STRING,
		city:
			getNestedValue(vendorData, 'vendor_address.city') ||
			getNestedValue(vendorData, 'vendor_info.address.city') ||
			EMPTY_STRING,
		state:
			getNestedValue(vendorData, 'vendor_address.state') ||
			getNestedValue(vendorData, 'vendor_info.address.state') ||
			EMPTY_STRING,
		business_unit_id: vendorData?.business_unit_id || 5,
	};

	// Debug: Log transformed values
	console.log('✅ [transformBasicInfo] Transformed values:', {
		vendor_email_id: transformed.vendor_email_id,
		primary_poc_name: transformed.primary_poc_name,
		primary_poc_contact: transformed.primary_poc_contact,
		vendor_address: transformed.vendor_address,
	});

	return transformed;
};

/**
 * Transform business information from vendor data
 */
export const transformBusinessInfo = (vendorData) => {
	// Map API GST type to form GST type (handle backward compatibility)
	const apiGstType = getNestedValue(vendorData, 'document_info.gst.gst_type');
	const gstTypeMap = {
		Registered: 'Registered',
		Composition: 'Composition',
		Unregistered: 'Unregistered',
	};
	const formGstType = gstTypeMap[apiGstType] || apiGstType;

	return {
		pan_document_type: 'PAN',
		pan_number: getNestedValue(vendorData, 'document_info.pan.number'),
		pan_file: null,
		pan_file_key: getNestedValue(vendorData, 'document_info.pan.document_key', null),
		gst_document_type: 'GST',
		gst_type: formGstType,
		gst_number: getNestedValue(vendorData, 'document_info.gst.number'),
		gst_file: null,
		gst_file_key: getNestedValue(vendorData, 'document_info.gst.document_key', null),
		aadhar_document_type: 'Aadhar',
		aadhar_number: getNestedValue(vendorData, 'document_info.aadhar.number'),
		aadhar_file: null,
		aadhar_file_key: getNestedValue(vendorData, 'document_info.aadhar.document_key', null),
		udf_file: null,
		udf_file_key: getNestedValue(vendorData, 'document_info.udf.document_key', null),
		fds_applicable: vendorData?.fds_applicable || false,
	};
};

/**
 * Transform banking information from vendor data
 */
export const transformBankingInfo = (vendorData) => ({
	account_holder_name: getNestedValue(vendorData, 'banking_info.account_holder_name'),
	account_number: getNestedValue(vendorData, 'banking_info.account_number'),
	bank_name: getNestedValue(vendorData, 'banking_info.bank_name'),
	ifsc_code: getNestedValue(vendorData, 'banking_info.ifsc_code'),
	branch_name: getNestedValue(vendorData, 'banking_info.branch_name'),
	account_type: getNestedValue(vendorData, 'banking_info.account_type'),
	bank_document_file: null,
	bank_document_key: getNestedValue(
		vendorData,
		'banking_info.document_info.branch_document.document_key',
		null
	),
	tds_applicable: vendorData?.fds_applicable || false,
	vendor_registered_msme: vendorData?.vendor_registered_msme || false,
	msme_number: vendorData?.msme_number || EMPTY_STRING,
	vrf_document_file: null,
	vrf_document_key: getNestedValue(vendorData, 'document_info.udf.document_key', null),
});

/**
 * Helper: Extract document key from vehicle (handles both document_info structure and direct fields)
 */
const getDocumentKey = (vehicle, docType) =>
	vehicle.document_info?.[docType]?.document_key || vehicle[`${docType}_document_key`] || null;

/**
 * Helper: Extract document valid_till from vehicle
 */
const getDocumentValidTill = (vehicle, docType) =>
	vehicle.document_info?.[docType]?.valid_till || vehicle[`${docType}_valid_till`] || null;

/**
 * Transform vehicle from API response to form format
 */
export const transformVehicleToFormFormat = (vehicle) => {
	if (!vehicle) return null;

	const vehicleType = vehicle.vehicle_type_name || vehicle.vehicleType || '';
	const vehicleNumber = vehicle.vehicle_number || vehicle.vehicleNumber || '';
	const ownershipType = vehicle.ownership_type || vehicle.ownershipType || '';

	return {
		key: vehicle.id?.toString() || `vehicle_${Date.now()}`,
		id: vehicle.id,
		vehicleType,
		vehicleNumber,
		rcDocument: !!getDocumentKey(vehicle, 'rc'),
		insurance: !!getDocumentKey(vehicle, 'insurance'),
		ownershipType,
		vehicle_type_id: vehicle.vehicle_type_id || vehicle.vehicleTypeId || null,
		vehicle_type_name: vehicleType,
		vehicle_number: vehicleNumber,
		rc_document_key: getDocumentKey(vehicle, 'rc'),
		rc_valid_till: getDocumentValidTill(vehicle, 'rc'),
		insurance_document_key: getDocumentKey(vehicle, 'insurance'),
		insurance_valid_till: getDocumentValidTill(vehicle, 'insurance'),
		puc_document_key: getDocumentKey(vehicle, 'puc'),
		puc_valid_till: getDocumentValidTill(vehicle, 'puc'),
		ownership_type: ownershipType,
		consent_letter_key: getDocumentKey(vehicle, 'consent_letter'),
		gps_installed: vehicle.gps_installed,
		gps_provider_name: vehicle.gps_provider_name || '',
		documents_complete: vehicle.documents_complete,
		skip_document_upload: !!vehicle.skip_document_upload,
	};
};

/**
 * Transform route from API response to form format
 */
export const transformRouteToFormFormat = (route) => {
	if (!route) return null;

	return {
		key: route.id?.toString() || `route_${Date.now()}`,
		id: route.id,
		vendorName: route.vendor_name || route.vendorName || '',
		typeOfMovement: route.type_of_movement || route.typeOfMovement || '',
		sourceCity: route.source_city || route.sourceCity || '',
		destinationCity: route.destination_city || route.destinationCity || '',
	};
};

/**
 * Transform vehicle and serviceability information from vendor data
 */
export const transformVehicleAndServiceabilityInfo = (vendorData) => ({
	total_vehicles: vendorData?.total_vehicles || EMPTY_STRING,
	vehicle_types: Array.isArray(vendorData?.vehicle_types) ? vendorData.vehicle_types : [],
	owns_vehicles: vendorData?.owns_vehicles !== undefined ? vendorData.owns_vehicles : true,
	vehicles: Array.isArray(vendorData?.vehicles) ? vendorData.vehicles : [],
	serviceable_cities: Array.isArray(vendorData?.serviceable_cities)
		? vendorData.serviceable_cities
		: [],
	serviceable_states: Array.isArray(vendorData?.serviceable_states)
		? vendorData.serviceable_states
		: [],
	operational_hours_from: vendorData?.operational_hours_from || EMPTY_STRING,
	operational_hours_to: vendorData?.operational_hours_to || EMPTY_STRING,
	routes: Array.isArray(vendorData?.routes)
		? vendorData.routes.map((route, index) => ({
				key: route.id?.toString() || route.key || index.toString(),
				id: route.id,
				vendorName: route.vendor_name || route.vendorName || '',
				typeOfMovement: route.type_of_movement || route.typeOfMovement || '',
				sourceCity: route.source_city || route.sourceCity || '',
				destinationCity: route.destination_city || route.destinationCity || '',
		  }))
		: [],
});

/**
 * Transform API vendor data to form values
 */
export const transformVendorDataToFormValues = (vendorData) => {
	if (!vendorData) return null;

	return {
		...transformBasicInfo(vendorData),
		...transformBusinessInfo(vendorData),
		...transformBankingInfo(vendorData),
		...transformVehicleAndServiceabilityInfo(vendorData),
		bank_details_approval_status: vendorData.bank_details_approval_status || null,
	};
};
